import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import VerificationSDK from "../VerificationSDK";
import "./kycModal.css";

interface KycFlowProps {
  sdk?: VerificationSDK;
  serverKey?: string;
  sessionId?: string;
  startAtQr?: boolean;
  pollIntervalMs?: number;
  onClose?: () => void;
  apiBaseUrl?: string;
  mobileBaseUrl?: string;
}

export const KycFlow: React.FC<KycFlowProps> = ({ 
  sdk: providedSdk, 
  serverKey, 
  sessionId: externalSessionId,
  startAtQr = true, 
  pollIntervalMs = 20000, 
  onClose, 
  apiBaseUrl,
  mobileBaseUrl = "https://api.probusiness.astraprotocol.com"
}) => {
  // console.log('[KycFlow] render', { 
  //   hasProvidedSdk: Boolean(), 
  //   hasServerKey: Boolean(providedSdk?.serverKey), 
  //   externalSessionId, 
  //   startAtQr, 
  //   apiBaseUrl 
  // });

  const [sdkInstance, setSdkInstance] = useState<VerificationSDK | null>(null);
  const [showQrScreen, setShowQrScreen] = useState<boolean>(startAtQr);
  const [waitingForMobile, setWaitingForMobile] = useState<boolean>(false);
  const [finalStatus, setFinalStatus] = useState<any>(null);
  const hasClosedRef = useRef(false);

  const clearSessionArtifacts = useCallback(() => {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
    try {
      window.localStorage.removeItem("kyc_session_id");
      window.localStorage.removeItem("kyc_face_done");
      window.localStorage.removeItem("kyc_qrcode_url");
    } catch (error) {
      try {
        console.warn("[KycFlow] clearSessionArtifacts:error", error);
      } catch {}
    }
  }, []);

  const closeFlow = useCallback(
    (reason: "user-dismiss" | "completed" = "user-dismiss") => {
      if (hasClosedRef.current) return;
      hasClosedRef.current = true;
      try {
        sdkInstance?.resetSession?.();
      } catch (error) {
        try {
          console.warn("[KycFlow] closeFlow:resetSession:error", error);
        } catch {}
      }

      clearSessionArtifacts();
      setShowQrScreen(false);
      setWaitingForMobile(false);
      setFinalStatus(null);

      try {
        // console.log("[KycFlow] closeFlow", { reason });
      } catch {}

      if (onClose) {
        onClose();
      }
    },
    [clearSessionArtifacts, onClose, sdkInstance]
  );

  useEffect(() => {
    hasClosedRef.current = false;
  }, [sdkInstance, externalSessionId]);

  useEffect(() => {
    if (showQrScreen || waitingForMobile || finalStatus) {
      hasClosedRef.current = false;
    }
  }, [showQrScreen, waitingForMobile, finalStatus]);

  const isTerminalStatus = useCallback((result: any): boolean => {
    if (!result) return false;
    const data: any = result?.data ?? result ?? {};
    const rawValues = [
      data?.kyc_status,
      data?.status,
      data?.result,
      data?.overall_status,
      data?.face_status,
      data?.decision,
    ];

    const terminalTokens = ["approved", "completed", "complete", "success", "verified", "passed", "rejected", "failed", "declined"];

    return rawValues.some((value) => {
      if (typeof value !== "string") return false;
      const normalized = value.trim().toLowerCase();
      if (!normalized) return false;
      if (
        normalized.includes("pending") ||
        normalized.includes("progress") ||
        normalized.includes("processing") ||
        normalized.includes("awaiting") ||
        normalized.includes("incomplete")
      ) {
        return false;
      }
      const tokens = normalized.split(/[^a-z]+/).filter(Boolean);
      return tokens.some((token) => terminalTokens.includes(token));
    });
  }, []);

  const handleResult = useCallback(
    (result: any) => {
      setShowQrScreen(false);
      if (!result) {
        setFinalStatus(null);
        setWaitingForMobile(false);
        return;
      }

      const terminal = isTerminalStatus(result);
      try {
        // console.log("[KycFlow] handleResult", { terminal });
      } catch {}

      if (terminal) {
        const redirectUrl: string | undefined = result?.data?.redirect_url ?? result?.data?.redirectUrl ?? result?.redirect_url;
        if (redirectUrl && typeof window !== "undefined") {
          try {
            window.location.href = String(redirectUrl);
            return;
          } catch (e) {
          }
        }

        closeFlow("completed");
        return;
      }

      setFinalStatus(result);
      setWaitingForMobile(false);
    },
    [closeFlow, isTerminalStatus]
  );

  useEffect(() => {
    // console.log('[KycFlow] effect:initializeSdk', { 
    //   providedSdk: Boolean(providedSdk), 
    //   serverKey, 
    //   externalSessionId, 
    //   apiBaseUrl 
    // });

    if (providedSdk) {
      setSdkInstance(providedSdk);
      if (externalSessionId) {
        providedSdk.setSessionId(externalSessionId);
        try { localStorage.setItem("kyc_session_id", externalSessionId); } catch {}
      }
    } else if (serverKey) {
      const newSdk = new VerificationSDK({ 
        serverKey: serverKey, 
        apiBaseUrl 
      });
      setSdkInstance(newSdk);
      
      if (externalSessionId) {
        newSdk.setSessionId(externalSessionId);
        try { localStorage.setItem("kyc_session_id", externalSessionId); } catch {}
      }
    }
  }, [providedSdk, serverKey, apiBaseUrl, externalSessionId]);

  useEffect(() => {
    if (!sdkInstance) return;
    
    try {
      const faceDone = localStorage.getItem("kyc_face_done");
      const sid = localStorage.getItem("kyc_session_id");
      // console.log('[KycFlow] effect:resumeIfFaceDone', { faceDone, sid, hasSdk: Boolean(sdkInstance) });
      
      if (sid && faceDone === "1") {
        sdkInstance.setSessionId(sid);
        setShowQrScreen(false);
        setWaitingForMobile(true);
      }
    } catch (error) {
      console.error('Error resuming flow:', error);
    }
  }, [sdkInstance]);

  useEffect(() => {
    if (!sdkInstance || !externalSessionId) return;
    
    // console.log('[KycFlow] effect:setExternalSessionId', { externalSessionId });
    
    try {
      const currentSessionId = sdkInstance.getSessionId();
      if (!currentSessionId || currentSessionId !== externalSessionId) {
        sdkInstance.setSessionId(externalSessionId);
        try { localStorage.setItem("kyc_session_id", externalSessionId); } catch {}
      }
      
      if (startAtQr) {
        setShowQrScreen(true);
      }
    } catch (error) {
      console.error('Error setting session ID:', error);
    }
  }, [sdkInstance, externalSessionId, startAtQr]);

  if (!sdkInstance) {
    return (
      <div className="kyc-backdrop">
        <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff" }}>
          <p>Initializing KYC session...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showQrScreen && (
        <QrStartScreen 
          sdk={sdkInstance} 
          serverKey={providedSdk?.serverKey} 
          pollIntervalMs={pollIntervalMs} 
          onClose={() => closeFlow("user-dismiss")} 
          mobileBaseUrl={mobileBaseUrl}
          onDone={(maybeResult) => {
            // console.log('[KycFlow] onDoneFromQr', { hasResult: Boolean(maybeResult) });
            setShowQrScreen(false);
            if (maybeResult) {
              handleResult(maybeResult);
            } else {
              setWaitingForMobile(true);
            }
          }} 
        />
      )}

      {waitingForMobile && (
        <DesktopWaitingForMobile
          sdk={sdkInstance}
          onShowStatus={(st) => { handleResult(st); }}
          onRetryScan={() => { setShowQrScreen(true); setWaitingForMobile(false); }}
          pollIntervalMs={pollIntervalMs}
        />
      )}

      {finalStatus && (
        <FinalStatusScreen 
          finalStatus={finalStatus} 
          onBack={() => { setFinalStatus(null); setWaitingForMobile(false); setShowQrScreen(true); }}
          onClose={() => closeFlow("user-dismiss")}
        />
      )}
    </div>
  );
};

interface QrStartScreenProps {
  sdk: VerificationSDK;
  pollIntervalMs?: number;
  onDone: (result?: any) => void;
  onClose?: () => void;
  serverKey?: string;
  mobileBaseUrl: string;
}

const QrStartScreen: React.FC<QrStartScreenProps> = ({ 
  sdk, 
  pollIntervalMs = 20000, 
  onDone, 
  onClose, 
  serverKey,
  mobileBaseUrl
}) => {
  const sessionId = sdk.getSessionId() || localStorage.getItem("kyc_session_id");
  const [qrUrl, setQrUrl] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  // console.log("write by me tayyab",sdk)
  // console.log('[QrStartScreen] render', { sessionId, serverKey });

useEffect(() => {
  if (!sessionId) {
    // console.log('[QrStartScreen] No session ID available');
    return;
  }

  const effectiveServerKey = (serverKey || (sdk as any).getServerKey?.()) as string | undefined;
  const keyParam = effectiveServerKey ? `&key=${encodeURIComponent(effectiveServerKey)}` : '';
  const mobileUrl = `${mobileBaseUrl.replace(/\/$/, '')}/mobile-kyc/${encodeURIComponent(sessionId)}?mode=face${keyParam}&apiBaseUrl=${encodeURIComponent("https://api.probusiness.astraprotocol.com")}`;
  
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(mobileUrl)}`;
  
  setQrUrl(qrCodeUrl);
  setError(null);
}, [sessionId, serverKey, sdk, mobileBaseUrl]);

  useEffect(() => {
    if (!sessionId) {
      // console.log('[QrStartScreen] No session ID for polling');
      return;
    }

    const effectiveServerKey = serverKey || sdk.getServerKey();
    if (!effectiveServerKey) {
      // console.error('[QrStartScreen] No server key for polling');
      return;
    }

    // console.log('[QrStartScreen] Starting polling', { sessionId, pollIntervalMs });

    let id: number | null = null;
    const transientRef = { count: 0 };

    const poll = async () => {
      try {
        const status: any = await sdk.getStatus(sdk?.getServerKey());
        transientRef.count = 0;
        setError(null);
        
        const data: any = status?.data ?? status;
        const faceStatus: string | undefined = data?.face_recognition_status || data?.face_status || data?.face || data?.faceRecognitionStatus;
        
        // console.log('[QrStartScreen] poll:status', { faceStatus });
        
        if (typeof faceStatus === "string" && faceStatus.toUpperCase() === "COMPLETED") {
          try { localStorage.setItem("kyc_face_done", "1"); } catch {}
          onDone();
          return;
        }
      } catch (e: any) {
        const msg = e?.message ? String(e.message) : "Unknown error";
        // console.log('[QrStartScreen] poll:error', msg);
        
        const isTransient = /unexpected token\s*</i.test(msg) || 
                           /failed to fetch/i.test(msg) || 
                           /networkerror/i.test(msg) || 
                           /not a valid json/i.test(msg) ||
                           /unexpected token/i.test(msg) || 
                           /json parse/i.test(msg);
        
        if (msg && /expired/i.test(msg)) {
          toast.error(msg);
        } else if (isTransient) {
          transientRef.count += 1;
          if (transientRef.count >= 6) {
            toast.warning("Network problem while checking status — retrying...");
          }
        } else {
          toast.error(msg || "Error polling status");
        }
      }
      
      id = window.setTimeout(poll, Math.max(1000, pollIntervalMs));
    };

    poll();
    
    return () => {
      if (id) window.clearTimeout(id);
    };
  }, [sdk, onDone, serverKey, sessionId, pollIntervalMs]);

  const effectiveServerKey = (serverKey || (sdk as any).getServerKey?.()) as string | undefined;
  const mobileUrl = sessionId
    ? `${mobileBaseUrl.replace(/\/$/, '')}/mobile-kyc/${sessionId}?mode=face${effectiveServerKey ? `&key=${encodeURIComponent(effectiveServerKey)}` : ''}`
    : "";

  const handleManualRefresh = async () => {
    // console.log('[QrStartScreen] manualRefresh');
    try {
      const result = await sdk.getResult(sdk?.getServerKey());

      const kycStatus: string | undefined = result?.data?.kyc_status ?? result?.kyc_status ?? result?.status;
      const redirectUrl: string | undefined = result?.data?.redirect_url ?? result?.data?.redirectUrl ?? result?.redirect_url;
      const norm = (s: string) => String(s || "").toLowerCase();
      if (typeof kycStatus === "string" && norm(kycStatus).includes("completed")) {
        if (redirectUrl && typeof window !== "undefined") {
          window.location.href = String(redirectUrl);
          return;
        }
        onDone(result);
        return;
      }

      try {
        const st = await sdk.getStatus(sdk?.getServerKey());
        const completedSteps: string[] = Array.isArray(st?.data?.completed_steps)
          ? st.data.completed_steps
          : (Array.isArray(st?.data?.completedSteps) ? st.data.completedSteps : []);
        const cs = (completedSteps || []).map((s: any) => String(s || "").toLowerCase());
        if (cs.includes("face_scan") || cs.includes("face")) {
          if (!cs.includes("document_upload") && !cs.includes("document")) {
            onDone();
            return;
          }
        }
      } catch (e) {
      }

      onDone(result);
    } catch (e: any) {
      toast.error(e?.message || "Unable to refresh status");
    }
  };

  const copyMobileUrl = async () => {
    if (!mobileUrl) return;
    try {
      await navigator.clipboard.writeText(mobileUrl);
      toast.success("URL copied to clipboard");
    } catch {
      const ta = document.createElement('textarea');
      ta.value = mobileUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success("URL copied to clipboard");
    }
  };

  return (
    <div className="kyc-backdrop">
      <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff", position: "relative" }}>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8, background: "transparent", color: "#fff", border: "none", fontSize: 20, lineHeight: 1, cursor: "pointer" }}
        >
          ✕
        </button>
        
        <div style={{ textAlign: "center" }}>
          <h2 className="kyc-title" style={{ color: "#fff", marginBottom: 8 }}>Continue on Mobile</h2>
          <p style={{ margin: 0, color: "#9ca3af", fontSize:'14px',  }}>Scan this QR on your phone to capture your face and document</p>
        </div>
        
        <div style={{ textAlign: "center", marginTop: 16 }}>
          {error ? (
            <p style={{ color: '#ef4444' }}>{error}</p>
          ) : qrUrl ? (
            <div style={{ display: 'inline-block', padding: 12, borderRadius: 12, border: '1px solid #334155', background: '#0b0f17' }}>
              <div style={{ background: '#ffffff', padding: 12, borderRadius: 8 }}>
                <img src={qrUrl} alt="Scan QR to continue on mobile" style={{ width: 220, height: 220, display: 'block' }} />
              </div>
            </div>
          ) : (
            <p style={{ color: '#9ca3af' }}>Preparing QR...</p>
          )}
          
          {mobileUrl && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
              <span>Or open: </span>
              <a href={mobileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>
                {mobileUrl.substring(0, 30)}...
              </a>
              <button 
                onClick={copyMobileUrl} 
                title="Copy URL" 
                style={{ marginLeft: 6, background: 'transparent', color: '#9ca3af', border: '1px solid #475569', borderRadius: 6, padding: '2px 6px', cursor: 'pointer' }}
              >
                📋
              </button>
            </div>
          )}
          
           <div style={{ marginTop: 16 }}>
            <button
              type="button"
              onClick={handleManualRefresh}
              style={{ padding: '12px 16px', width: '100%', maxWidth: 360, borderRadius: 8, border: 'none', background: 'linear-gradient(90deg, #FF8A00 0%, #FF3D77 100%)', color: '#ffffff', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}
            >
              I've completed on mobile - Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FinalStatusScreen: React.FC<{ 
  finalStatus: any; 
  onBack: () => void; 
  onClose: () => void; 
}> = ({ finalStatus, onBack, onClose }) => {
  const data: any = finalStatus?.data ?? finalStatus;
  const email: string | undefined = data?.email;
  const faceStatus: string = (String(data?.face_recognition_status || data?.face_status || data?.face || "Pending")).trim();
  const kycStatus: string = (String(data?.kyc_status || data?.status || "Pending")).trim();
  
  const pill = (label: string, value: string) => (
    <div style={{ background: "#111827", border: "1px solid #30363d", borderRadius: 12, padding: "14px 16px", color: "#e5e7eb", boxShadow: "0 8px 16px rgba(0,0,0,0.35)" }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      <span style={{ marginLeft: 8, color: label.toLowerCase().includes("status") ? "#f97316" : "#e5e7eb" }}>
        {value || "—"}
      </span>
    </div>
  );

  return (
    <div className="kyc-backdrop">
      <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff", position: "relative" }}>
        <div style={{ position: "absolute", left: 12, top: 12, cursor: "pointer", fontSize: 20 }} onClick={onBack} aria-label="Back">←</div>
        <div style={{ position: "absolute", right: 12, top: 12, cursor: "pointer" }} onClick={onClose} aria-label="Close">✕</div>
        <h2 className="kyc-title" style={{ color: "#fff", textAlign: "center", marginBottom: 16 }}>Verification Status</h2>
        <div style={{ display: "grid", gap: 12 }}>
          {pill("Email:", email || "—")}
          {pill("Face Status:", faceStatus || "Pending")}
          {pill("KYC Status:", kycStatus || "Pending")}
        </div>
      </div>
    </div>
  );
};

interface DesktopWaitingProps { 
  sdk: VerificationSDK; 
  onShowStatus: (status: any) => void; 
  pollIntervalMs?: number; 
}

interface DesktopWaitingExtraProps extends DesktopWaitingProps {
  onRetryScan?: () => void;
}

const WAITING_DEFAULT_MESSAGE = "Scan the QR code on your mobile device to continue your verification.";

const deriveWaitingFlags = (payload: any): { faceDone: boolean; docDone: boolean; kycDone: boolean } | null => {
  if (!payload) return null;
  const data: any = payload?.data ?? payload ?? {};
  const completedStepsRaw: any[] = Array.isArray(data?.completed_steps)
    ? data.completed_steps
    : (Array.isArray(data?.completedSteps) ? data.completedSteps : []);
  const normalize = (value: any) => String(value || "").toLowerCase();
  const steps = completedStepsRaw.map(normalize);
  const includesStep = (needle: string) => steps.some((step) => step.includes(needle));
  let faceDone = includesStep("face");
  let docDone = includesStep("doc");

  const faceStatus = normalize(data?.face_status || data?.face_recognition_status || data?.face);
  if (faceStatus && (faceStatus.includes("complete") || faceStatus.includes("success") || faceStatus.includes("approved") || faceStatus.includes("verified"))) {
    faceDone = true;
  }

  const docStatus = normalize(data?.document_status || data?.docs_status || data?.document || data?.doc_status);
  if (docStatus && (docStatus.includes("complete") || docStatus.includes("success") || docStatus.includes("approved") || docStatus.includes("uploaded") || docStatus.includes("done"))) {
    docDone = true;
  }

  const nextStep = normalize(data?.next_step || data?.nextStep);
  if (nextStep.includes("face")) faceDone = false;
  if (nextStep.includes("doc")) docDone = false;

  const kycStatus = normalize(data?.kyc_status || data?.status || data?.result);
  const kycDone = Boolean(kycStatus) && ["approved", "completed", "complete", "success", "verified", "passed"].some((token) => kycStatus.includes(token));

  const hasSignal = steps.length > 0 || Boolean(faceStatus) || Boolean(docStatus) || Boolean(kycStatus) || Boolean(nextStep);
  if (!hasSignal) return null;

  return { faceDone, docDone, kycDone };
};

const describeWaitingMessage = (payload: any): string => {
  const flags = deriveWaitingFlags(payload);
  if (!flags) return WAITING_DEFAULT_MESSAGE;
  if (!flags.faceDone) {
    return "Please complete the face capture on your mobile device to move forward.";
  }
  if (flags.faceDone && !flags.docDone) {
    return "Face is completed. Upload your document on mobile, then refresh.";
  }
  if (flags.docDone && !flags.kycDone) {
    return "Document uploaded. We're processing it — click refresh soon for an update.";
  }
  return "We're finalizing your verification. Click refresh to view the decision.";
};

const DesktopWaitingForMobile: React.FC<DesktopWaitingExtraProps> = ({ sdk, onShowStatus, onRetryScan, pollIntervalMs = 20000 }) => {
  const [statusHint, setStatusHint] = useState<string>(WAITING_DEFAULT_MESSAGE);
  const [message, setMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      try {
        const status = await sdk.getStatus(sdk?.getServerKey());
        if (!cancelled) {
          setStatusHint(describeWaitingMessage(status));
        }
      } catch {
        if (!cancelled) {
          setStatusHint(WAITING_DEFAULT_MESSAGE);
        }
      }
    };
    hydrate();
    return () => { cancelled = true; };
  }, [sdk]);

  const checkNow = async () => {
    setLoading(true);
    try {
      const result = await sdk.getResult(sdk?.getServerKey());
      setStatusHint(describeWaitingMessage(result));

      const kycStatus: string | undefined = result?.data?.kyc_status ?? result?.kyc_status ?? result?.status;
      const redirectUrl: string | undefined = result?.data?.redirect_url ?? result?.data?.redirectUrl ?? result?.redirect_url;
      const norm = (s: string) => String(s || "").toLowerCase();
      if (typeof kycStatus === "string" && norm(kycStatus).includes("completed")) {
        if (redirectUrl && typeof window !== "undefined") {
          window.location.href = String(redirectUrl);
          return;
        }
        onShowStatus(result);
        return;
      }

      try {
        const st = await sdk.getStatus(sdk?.getServerKey());
        setStatusHint(describeWaitingMessage(st));
        const flags = deriveWaitingFlags(st);
        if (flags && flags.faceDone && !flags.docDone) {
          if (onRetryScan) onRetryScan();
          setStatusHint(WAITING_DEFAULT_MESSAGE);
          setMessage("Scan the QR code on your mobile device to resume the document step.");
          return;
        }
      } catch (e) {
      }

      onShowStatus(result);
    } catch (e: any) {
      setMessage("Still waiting for mobile to finish. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="kyc-backdrop">
      <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff" }}>
        <h2 className="kyc-title" style={{ color: "#fff" }}>Continue on Mobile</h2>
        <p style={{ color: "#9ca3af", marginTop: 0 }}>{statusHint}</p>
        {message && <p style={{ color: "#9ca3af" }}>{message}</p>}
        <div style={{ marginTop: 12 }}>
          <button 
            type="button" 
            onClick={checkNow} 
            disabled={loading} 
            className="kyc-input" 
            style={{ background: "linear-gradient(90deg, #FF8A00 0%, #FF3D77 100%)", color: "#fff", border: "none", borderRadius: 8, padding: 10 }}
          >
            {loading ? "Checking..." : "I've completed on mobile - Refresh"}
          </button>
        </div>
      </div>
    </div>
  );
};